import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CirclePlus, ImageUp, LoaderCircle, X } from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";

const warehouseTypes = [
  ["principal", "Dépôt principal"],
  ["zone_conakry", "Zone Conakry"],
  ["zone_interieur", "Zone Intérieur"],
  ["livreur", "Dépôt livreur"],
];

export default function ProductFullEditModal({ product, onClose, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    ...product,
    tags: product.tags || [],
    thresholds: product.thresholds || {},
  });
  const [tag, setTag] = useState("");
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    api
      .get("/categories")
      .then((response) => setCategories(response.data))
      .catch((error) => toast.error(getError(error)));
  }, []);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose();
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose, saving]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const addTag = () => {
    const value = tag.trim().toLowerCase();

    if (value && !form.tags.includes(value)) {
      setForm((current) => ({
        ...current,
        tags: [...current.tags, value],
      }));
    }

    setTag("");
  };

  const removeTag = (value) => {
    setForm((current) => ({
      ...current,
      tags: current.tags.filter((item) => item !== value),
    }));
  };

  const updateThreshold = (key, value) => {
    setForm((current) => ({
      ...current,
      thresholds: {
        ...current.thresholds,
        [`type:${key}`]: Number(value),
      },
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveError("");

    try {
      const payload = {
        ...form,
        purchase_price: Number(form.purchase_price),
        sale_price: Number(form.sale_price),
        minimum_sale_price: Number(form.minimum_sale_price || 0),
      };
      const response = await api.put(`/products/${product.product_id}`, payload);

      if (photo) {
        try {
          const photoData = new FormData();
          photoData.append("file", photo);
          await api.post(`/products/${product.product_id}/photo`, photoData);
        } catch (photoError) {
          const message = "Les informations ont été enregistrées, mais la photo n’a pas pu être envoyée.";
          setSaveError(message);
          toast.error(message);
          return;
        }
      }

      toast.success("Fiche produit modifiée");
      onSaved(response.data);
    } catch (error) {
      const message = getError(error);
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      aria-labelledby="edit-product-modal-title"
      aria-modal="true"
      className="product-edit-modal"
      data-testid="edit-product-modal"
      role="dialog"
    >
      <div className="product-edit-dialog" data-testid="edit-product-dialog">
        <header className="product-edit-header">
          <div>
            <p className="eyebrow">FICHE PRODUIT</p>
            <h2 id="edit-product-modal-title">Modifier {form.name || "le produit"}</h2>
            <p>Les informations sont enregistrées sur cette fiche.</p>
          </div>
          <button
            aria-label="Fermer la modification"
            className="product-edit-close-button"
            data-testid="close-product-edit-button"
            disabled={saving}
            onClick={onClose}
            type="button"
          >
            <X size={20} />
          </button>
        </header>

        <form className="product-edit-form" data-testid="full-product-edit-form" onSubmit={save}>
          <div className="product-edit-scroll" data-testid="edit-product-scroll">
            <section className="product-edit-section" data-testid="edit-product-information-section">
              <div className="product-edit-section-heading">
                <h3>Informations essentielles</h3>
                <span>Nom, catégorie et description</span>
              </div>
              <div className="product-edit-grid">
                <label className="product-edit-field product-edit-field-wide">
                  <span>Nom du produit</span>
                  <input
                    data-testid="edit-product-name-input"
                    onChange={(event) => updateField("name", event.target.value)}
                    placeholder="Nom du produit"
                    required
                    value={form.name || ""}
                  />
                </label>
                <label className="product-edit-field">
                  <span>Catégorie</span>
                  <select
                    data-testid="edit-product-category-select"
                    onChange={(event) => updateField("category_id", event.target.value || null)}
                    value={form.category_id || ""}
                  >
                    <option value="">Sans catégorie</option>
                    {categories.map((category) => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="product-edit-field">
                  <span>Photo</span>
                  <span className="product-edit-file-control">
                    <ImageUp aria-hidden="true" size={16} />
                    <span>{photo ? photo.name : "Choisir une image"}</span>
                    <input
                      accept="image/jpeg,image/png,image/webp"
                      data-testid="edit-product-photo-input"
                      onChange={(event) => setPhoto(event.target.files?.[0] || null)}
                      type="file"
                    />
                  </span>
                </label>
                <label className="product-edit-field product-edit-field-wide">
                  <span>Description</span>
                  <textarea
                    data-testid="edit-product-description-input"
                    onChange={(event) => updateField("description", event.target.value)}
                    placeholder="Description du produit"
                    value={form.description || ""}
                  />
                </label>
                <label className="product-edit-field product-edit-field-wide">
                  <span>Mode d’emploi</span>
                  <textarea
                    data-testid="edit-product-usage-instructions-input"
                    onChange={(event) => updateField("usage_instructions", event.target.value)}
                    placeholder="Mode d’emploi ou conseils d’utilisation"
                    value={form.usage_instructions || ""}
                  />
                </label>
              </div>
            </section>

            <section className="product-edit-section" data-testid="edit-product-pricing-section">
              <div className="product-edit-section-heading">
                <h3>Prix de référence</h3>
                <span>Montants en GNF</span>
              </div>
              <div className="product-edit-grid product-edit-pricing-grid">
                <label className="product-edit-field">
                  <span>Prix d’achat</span>
                  <input
                    data-testid="edit-product-purchase-price-input"
                    min="0"
                    onChange={(event) => updateField("purchase_price", event.target.value)}
                    type="number"
                    value={form.purchase_price ?? ""}
                  />
                </label>
                <label className="product-edit-field">
                  <span>Prix de vente</span>
                  <input
                    data-testid="edit-product-sale-price-input"
                    min="0"
                    onChange={(event) => updateField("sale_price", event.target.value)}
                    type="number"
                    value={form.sale_price ?? ""}
                  />
                </label>
                <label className="product-edit-field">
                  <span>Prix minimum autorisé</span>
                  <input
                    data-testid="edit-product-minimum-sale-price-input"
                    min="0"
                    onChange={(event) => updateField("minimum_sale_price", event.target.value)}
                    type="number"
                    value={form.minimum_sale_price ?? 0}
                  />
                </label>
              </div>
            </section>

            <section className="product-edit-section" data-testid="edit-product-tags-section">
              <div className="product-edit-section-heading">
                <h3>Tags de recherche</h3>
                <span>Utiles pour retrouver rapidement le produit</span>
              </div>
              <div className="product-edit-tags">
                <div className="product-edit-tag-input-row">
                  <input
                    data-testid="edit-product-tag-input"
                    onChange={(event) => setTag(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Ajouter un tag"
                    value={tag}
                  />
                  <button
                    aria-label="Ajouter le tag"
                    className="product-edit-add-tag-button"
                    data-testid="add-edit-product-tag-button"
                    onClick={addTag}
                    type="button"
                  >
                    <CirclePlus size={18} />
                  </button>
                </div>
                <div className="tag-list" data-testid="edit-product-tags-list">
                  {form.tags.map((value) => (
                    <span key={value}>
                      {value}
                      <button
                        aria-label={`Supprimer le tag ${value}`}
                        data-testid={`remove-edit-product-tag-${value}`}
                        onClick={() => removeTag(value)}
                        type="button"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <section className="product-edit-section" data-testid="edit-product-thresholds-section">
              <div className="product-edit-section-heading">
                <h3>Seuils d’alerte</h3>
                <span>Quantité minimale par type de dépôt</span>
              </div>
              <div className="product-edit-threshold-grid">
                {warehouseTypes.map(([key, label]) => (
                  <label className="product-edit-field" key={key}>
                    <span>{label}</span>
                    <input
                      data-testid={`edit-product-threshold-${key}-input`}
                      min="0"
                      onChange={(event) => updateThreshold(key, event.target.value)}
                      type="number"
                      value={form.thresholds[`type:${key}`] ?? 0}
                    />
                  </label>
                ))}
              </div>
            </section>

            {saveError && (
              <p className="product-edit-error" data-testid="edit-product-save-error" role="alert">
                {saveError}
              </p>
            )}
          </div>

          <footer className="product-edit-footer">
            <button
              className="secondary-action"
              data-testid="cancel-product-edit-button"
              disabled={saving}
              onClick={onClose}
              type="button"
            >
              Annuler
            </button>
            <button className="primary-action" data-testid="save-product-edit-button" disabled={saving} type="submit">
              {saving ? <LoaderCircle aria-hidden="true" className="product-edit-spinner" size={17} /> : null}
              {saving ? "Enregistrement…" : "Enregistrer les modifications"}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}